import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../lib/utils";

// The one button for landing and admin (F123): always a pill, three heights
// (sm 32, default 40, lg 48; lg is the page's main action, full width on
// phones via className). Landing (F177): default for card and panel buttons,
// sm only for small inline actions in lists; never hand-written padding.
// Landing (F183): this solid button submits or commits; a link to another page
// is landing's NavButton (ghost + chevron), not a styled link. Navy main buttons (F159; `black` keeps the former
// ones), gold only as the focus mark (globals.css). `loading` keeps the width, shows a spinner and blocks
// clicks.
const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-[background-color,color,transform] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        // The former main button (F123), kept for a rollback or a one-off (F159).
        black: "bg-black text-black-foreground hover:bg-black-hover",
        secondary: "bg-surface text-foreground hover:bg-muted",
        outline: "border border-border bg-background text-foreground hover:bg-muted",
        ghost: "text-foreground hover:bg-muted",
        danger: "bg-tint-red text-ink-red hover:bg-tint-red/70",
        link: "h-auto px-0 text-foreground underline underline-offset-4 hover:text-muted-foreground active:scale-100",
        // Floating glass pill, for controls that sit directly on the page
        // canvas (header actions) rather than inside a card.
        glass: "border border-border bg-background/70 text-foreground backdrop-blur-sm hover:bg-background/90",
      },
      size: {
        sm: "h-8 px-3.5 text-xs [&_svg]:size-3.5",
        // 40px, or 32px where an area sets data-density="compact" (F191).
        default: "h-(--size-control) px-5 text-sm [&_svg]:size-4",
        lg: "h-12 px-6 text-sm [&_svg]:size-5",
        icon: "size-9 [&_svg]:size-[18px]",
        // Same height as "icon", so header pills line up.
        pill: "h-9 px-4 text-sm [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Busy: a spinner over the label (the width stays), clicks blocked. */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && !asChild ? (
          <>
            <span className="invisible inline-flex items-center gap-2">{children}</span>
            <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
